import React from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface LoadingSpinnerProps {
  message?: string;
  size?: number;
}

const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  message,
  size = 40
}) => {
  const { t } = useTranslation('components', { keyPrefix: 'loadingSpinner' });
  const ringSize = Math.max(size, 56);

  return (
    <Box
      sx={{
        '@keyframes anLoadingPulse': {
          '0%, 100%': { transform: 'scale(0.86)', opacity: 0.72 },
          '50%': { transform: 'scale(1)', opacity: 1 },
        },
        '@keyframes anLoadingDot': {
          '0%, 80%, 100%': { transform: 'translateY(0)', opacity: 0.38 },
          '40%': { transform: 'translateY(-4px)', opacity: 1 },
        },
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background:
          'radial-gradient(circle at 50% 36%, rgb(var(--an-primary-rgb) / 0.12), var(--an-transparent) 24rem), var(--an-background)',
        zIndex: 9999,
        margin: 0,
        padding: 2,
        boxSizing: 'border-box',
      }}
    >
      <Box
        sx={{
          width: 'min(100%, 280px)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
          px: 3,
          py: 3.5,
          backgroundColor: 'rgb(var(--an-surface-rgb) / 0.92)',
          border: '1px solid var(--an-border-light)',
          borderRadius: 'var(--an-radius-md)',
          boxShadow: 'var(--an-shadow-sm)',
          backdropFilter: 'blur(18px)',
          WebkitBackdropFilter: 'blur(18px)',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: ringSize,
            height: ringSize,
            display: 'grid',
            placeItems: 'center',
          }}
        >
          <CircularProgress
            variant="determinate"
            value={100}
            size={ringSize}
            thickness={3.6}
            sx={{
              position: 'absolute',
              color: 'var(--an-primary-light)',
            }}
          />
          <CircularProgress
            size={ringSize}
            thickness={3.6}
            sx={{
              position: 'absolute',
              color: 'var(--an-primary)',
              '& .MuiCircularProgress-circle': {
                strokeLinecap: 'round',
              },
            }}
          />
          <Box
            sx={{
              width: ringSize * 0.42,
              height: ringSize * 0.42,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, var(--an-primary), var(--an-accent))',
              boxShadow: '0 0 0 8px rgb(var(--an-primary-rgb) / 0.08)',
              animation: 'anLoadingPulse 1400ms ease-in-out infinite',
            }}
          />
        </Box>

        <Typography
          variant="body1"
          sx={{
            color: 'var(--an-text-primary)',
            fontWeight: 700,
            letterSpacing: 0,
            textAlign: 'center',
          }}
        >
          {message ?? t('loading')}
        </Typography>

        <Box
          aria-hidden="true"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
          }}
        >
          {[0, 1, 2].map((index) => (
            <Box
              key={index}
              component="span"
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                backgroundColor: 'var(--an-accent)',
                animation: 'anLoadingDot 1200ms ease-in-out infinite',
                animationDelay: `${index * 140}ms`,
              }}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default LoadingSpinner;
